<script>
  import { fly } from 'svelte/transition';
  import { createEventDispatcher } from 'svelte';
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import RightPane from '../Panes/RightPane.svelte';
  import PickerPanel from '../Panes/PickerPanel.svelte';
  import { getEgresoDets } from '../../models/egresoDetModel';
  import { getEgresoEscoltas, getEgresoCorredores, getEgresoPickers } from '../../models/egresoCabModel';
  import { authStore } from '../../lib/authStore';
  import { getStatusColorClasses } from '../../config.js';

  const dispatch = createEventDispatcher();
  export let title;
  export let status;
  export let items = [];
  export let pickerMode = false;

  // Keep in sync with parent — never reassign `items` (breaks Svelte prop binding)
  let viewItems = [];
  $: viewItems = items || [];

  let selectedItem = null;
  let draggedItem = null;
  let isDragOver = false;
  let isAdmin = false;

  const PICKER_CLAIM_FROM = 'AUTORIZADO_POR_CREDITOS';
  const PICKER_CLAIM_TO = 'ASIGNACION_MESA';

  $: canPickerDrag = pickerMode && status === PICKER_CLAIM_FROM;
  $: canPickerDrop = pickerMode && status === PICKER_CLAIM_TO;
  $: canAdminDragDrop = !pickerMode && isAdmin;

  // Check admin role from localStorage
  function checkAdminRole() {
    const roleData = localStorage.getItem("pb_user_role");
    if (roleData) {
      try {
        const role = JSON.parse(roleData);
        isAdmin = role.descrip === 'Administrador';
      } catch (error) {
        isAdmin = false;
      }
    } else {
      isAdmin = false;
    }
  }

  // Check role when component mounts and periodically
  onMount(() => {
    checkAdminRole();
    // Check again after a short delay in case role was still loading
    setTimeout(checkAdminRole, 1000);
  });

  async function handleClick(item) {
    // En tablero mesa solo se abre el panel de picking en Asignación de mesa
    if (pickerMode && item.sta !== PICKER_CLAIM_TO && status !== PICKER_CLAIM_TO) {
      return;
    }

    const egresoDets = await getEgresoDets(item.id, 1, 100);
    const egresoEscolta = await getEgresoEscoltas(item.id, 1, 100);
    const egresoCorredor = await getEgresoCorredores(item.id, 1, 100);
    const egresoPicker = await getEgresoPickers(item.id, 1, 100);
    selectedItem = {
      ...item,
      egresoDet: egresoDets.items,
      escoltas: egresoEscolta.items,
      corredores: egresoCorredor.items,
      pickers: egresoPicker.items,
    };

    console.log("selectedItem",selectedItem)
  }

  function closePane() {
    selectedItem = null;
  }

  function handlePickerStatusChanged(event) {
    const { id } = event.detail;
    viewItems = viewItems.filter((item) => item.id !== id);
    selectedItem = null;
    dispatch('statusChanged', event.detail);
  }

  function handleObservUpdated(event) {
    const { id, observ } = event.detail;
    viewItems = viewItems.map((item) => (item.id === id ? { ...item, observ } : item));
    if (selectedItem?.id === id) {
      selectedItem = { ...selectedItem, observ };
    }
  }

  function formatDate(dateStr) {
    // Create date object from the input string (UTC from PocketBase)
    const date = new Date(dateStr);
    if (isNaN(date)) return '';
    
    // Convert from UTC to Guatemala timezone (UTC-6)
    const guatemalaDate = new Date(date.getTime() - (6 * 60 * 60 * 1000));
    
    // Format date components
    const day = String(guatemalaDate.getUTCDate()).padStart(2, '0');
    const month = String(guatemalaDate.getUTCMonth() + 1).padStart(2, '0');
    const year = guatemalaDate.getUTCFullYear();
    
    // Get hours in 12-hour format
    let hours = guatemalaDate.getUTCHours();
    const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    
    const minutes = String(guatemalaDate.getUTCMinutes()).padStart(2, '0');
    const seconds = String(guatemalaDate.getUTCSeconds()).padStart(2, '0');
    
    return `${day}/${month}/${year}, ${String(hours).padStart(2, '0')}:${minutes}:${seconds} ${ampm}`;
  }

  // Simple function to truncate text
  function truncateText(text, maxLength = 50) {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  function getEscoltaNames(item) {
    if (Array.isArray(item.escolta_names) && item.escolta_names.length > 0) {
      return item.escolta_names;
    }

    if (item.escolta_name && item.escolta_name !== 'Sin escolta') {
      return item.escolta_name.split(',').map((name) => name.trim()).filter(Boolean);
    }

    return [];
  }

  function getStatusColor() {
    return getStatusColorClasses(status);
  }

  const noteThemes = {
    ESPERANDO_AUTORIZACION_CREDITOS: { bg: '#ffe8a3', bgDark: '#ffd966', ink: '#5c4a00' },
    AUTORIZADO_POR_CREDITOS: { bg: '#b8f2e6', bgDark: '#7ee8d5', ink: '#0f5132' },
    RECHAZADO_POR_CREDITOS: { bg: '#ffc9c9', bgDark: '#ff9f9f', ink: '#7f1d1d' },
    ASIGNACION_MESA: { bg: '#ffd4a8', bgDark: '#ffb366', ink: '#6b3a00' },
    LISTO_PARA_FACTURAR: { bg: '#a7f3d0', bgDark: '#6ee7b7', ink: '#065f46' },
    FACTURADO: { bg: '#bae6fd', bgDark: '#7dd3fc', ink: '#0c4a6e' },
    DEFAULT: { bg: '#fff9c4', bgDark: '#fff176', ink: '#4a4a4a' },
  };

  function getNoteTheme() {
    return noteThemes[status] || noteThemes.DEFAULT;
  }

  function getNoteStyle() {
    const theme = getNoteTheme();
    return `--note-bg: ${theme.bg}; --note-bg-dark: ${theme.bgDark}; --note-ink: ${theme.ink};`;
  }

  let draggingId = null;

  function handleLocationUpdate(event) {
    console.log("Event payload in CardStatus:", event.detail);
    const { id, geo_target_ref, geo_delivery_ref } = event.detail;

    viewItems = viewItems.map(item => {
      if (item.id === id) {
        return {
          ...item,
          ...(geo_target_ref && { geo_target_ref }),
          ...(geo_delivery_ref && { geo_delivery_ref })
        };
      }
      return item;
    });

    dispatch('updateLocation', event.detail);
  }

  function handlePickerUpdated(event) {
    const { egresoId, pickerName } = event.detail;
    viewItems = viewItems.map((item) =>
      item.id === egresoId ? { ...item, picker_name: pickerName || 'Sin mesa' } : item
    );
  }

  function handleEgresoEscoltaUpdate(event) {
    console.log("Event payload in CardStatus (Escoltas Update):", event.detail);
    const { egresoId, escoltas } = event.detail;

    // Ensure the correct keys are updated in the items array
    viewItems = viewItems.map(item => {
      if (item.id === egresoId) {
        return {
          ...item,
          escoltas: escoltas // Update escoltas for the matching item
        };
      }
      return item;
    });

    console.log("Updated items in CardStatus (Escoltas):", items);

    // Dispatch the escoltas update to the parent component or store
    dispatch('updateEgresoEscolta', {
      egresoId: egresoId,
      escoltas: escoltas
    });
  }

  function handleDragStart(event, item) {
    if (canPickerDrag) {
      draggedItem = item;
      draggingId = item.id;
      event.dataTransfer.setData('text/plain', JSON.stringify(item));
      event.dataTransfer.effectAllowed = 'move';
      return;
    }

    if (!canAdminDragDrop) {
      event.preventDefault();
      return;
    }
    
    draggedItem = item;
    draggingId = item.id;
    event.dataTransfer.setData('text/plain', JSON.stringify(item));
    event.dataTransfer.effectAllowed = 'move';
  }

  function handleDragEnd(event) {
    draggingId = null;
    draggedItem = null;
  }

  function handleDragOver(event) {
    if (canPickerDrop) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      isDragOver = true;
      return;
    }

    if (!canAdminDragDrop) {
      return;
    }
    
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    isDragOver = true;
  }

  function handleDragLeave(event) {
    if (!canPickerDrop && !canAdminDragDrop) return;
    if (!event.currentTarget.contains(event.relatedTarget)) {
      isDragOver = false;
    }
  }

  function handleDrop(event) {
    event.preventDefault();
    isDragOver = false;

    try {
      const droppedItem = JSON.parse(event.dataTransfer.getData('text/plain'));

      if (canPickerDrop) {
        // Solo claim: Autorizado → Asignación de mesa (el padre actualiza ambas columnas)
        if (droppedItem.sta !== PICKER_CLAIM_FROM) return;
        if (status !== PICKER_CLAIM_TO) return;

        dispatch('pickerClaim', {
          itemId: droppedItem.id,
          item: droppedItem,
        });
        return;
      }

      if (!canAdminDragDrop) return;

      if (droppedItem.sta !== status) {
        const updatedItem = { ...droppedItem, sta: status };
        
        dispatch('statusChange', {
          itemId: droppedItem.id,
          oldStatus: droppedItem.sta,
          newStatus: status,
          item: updatedItem
        });
        
        if (droppedItem.sta !== status) {
          viewItems = viewItems.filter(item => item.id !== droppedItem.id);
        }
      }
    } catch (error) {
      console.error('Error processing dropped item:', error);
    }
  }
</script>

<style>
  .notes-board {
    background-color: rgba(255, 255, 255, 0.28);
    background-image: radial-gradient(rgba(0, 0, 0, 0.04) 1px, transparent 1px);
    background-size: 10px 10px;
  }

  .sticky-note {
    position: relative;
    border: none;
    border-radius: 8px;
    padding: 0.9rem 0.95rem 1.05rem;
    margin-bottom: 0.75rem;
    background: linear-gradient(
      145deg,
      var(--note-bg) 0%,
      var(--note-bg-dark) 100%
    );
    color: var(--note-ink);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08);
    transition: box-shadow 0.2s ease, transform 0.2s ease;
    list-style: none;
  }

  .sticky-note:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.14), 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 5;
  }

  .sticky-note.is-dragging {
    opacity: 0.72;
    transform: scale(0.98);
    z-index: 20;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  }

  .note-badge {
    background: rgba(255, 255, 255, 0.72);
    color: var(--note-ink);
    border: 1px dashed rgba(0, 0, 0, 0.15);
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  .note-label {
    color: rgba(0, 0, 0, 0.55);
  }

  .note-value {
    color: var(--note-ink);
  }

  .note-date {
    color: rgba(0, 0, 0, 0.5);
    font-style: italic;
  }

  .note-escolta {
    background: rgba(255, 255, 255, 0.55);
    color: var(--note-ink);
    border: 1px solid rgba(0, 0, 0, 0.08);
  }

  .drag-over {
    background-color: rgba(59, 130, 246, 0.1);
    border-color: rgb(59, 130, 246);
  }
</style>

  <div
    class={`relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded border ${getStatusColor()} ${isDragOver ? 'ring-2 ring-blue-500 ring-opacity-50 bg-blue-50' : ''}`}
    style="height: 480px;"
    on:dragover={handleDragOver}
    on:dragleave={handleDragLeave}
    on:drop={handleDrop}
  >
  <div class={`rounded-t mb-0 px-4 py-3 border-0 ${getStatusColor()}`}>
    <div class="flex flex-wrap items-center">
      <div class="relative w-full px-4 max-w-full flex-grow flex-1">
        <h3 class="font-semibold text-base">{title} ({viewItems.length})</h3>
      </div>
    </div>
  </div>

  <div class="notes-board block w-full overflow-y-auto p-4" style="height: calc(100% - 116px);">
    {#if viewItems.length > 0}
      <ul class="list-none m-0 p-0">
        {#each viewItems as item (item.id)}
          <li
            transition:fly={{ y: 20, duration: 600 }}
            class="sticky-note {canPickerDrag || canAdminDragDrop ? 'cursor-grab' : 'cursor-pointer'} {draggingId === item.id ? 'is-dragging' : ''}"
            style={getNoteStyle()}
            draggable={canPickerDrag || canAdminDragDrop}
            on:click={() => handleClick(item)}
            on:dragstart={(e) => handleDragStart(e, item)}
            on:dragend={handleDragEnd}
          >
            <div class="flex justify-between items-start mb-2">
              <div class="overflow-x-auto flex-1 mr-2">
                <span class="font-bold text-sm leading-tight whitespace-nowrap" title={item.nombre}>{item.nombre}</span>
              </div>
              <span class="note-badge text-xs px-2 py-1 rounded flex-shrink-0">{item.numero}</span>
            </div>

            <div class="note-date text-xs mb-3">{formatDate(item.created)}</div>

            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="note-label text-xs font-medium flex-shrink-0">Método:</span>
                <div class="overflow-x-auto flex-1 ml-2 text-right">
                  <span class="note-value text-xs font-semibold whitespace-nowrap">{item.delivery_method || 'Sin asignar'}</span>
                </div>
              </div>
              <div class="flex justify-between">
                <span class="note-label text-xs font-medium flex-shrink-0">Mesa:</span>
                <div class="overflow-x-auto flex-1 ml-2 text-right">
                  <span class="note-value text-xs font-semibold whitespace-nowrap">{item.picker_name || 'Sin mesa'}</span>
                </div>
              </div>
              <div class="flex justify-between">
                <span class="note-label text-xs font-medium flex-shrink-0">Delivery:</span>
                <div class="overflow-x-auto flex-1 ml-2 text-right">
                  <span class="note-value text-xs font-semibold whitespace-nowrap">{item.delivery_name || 'Sin asignar'}</span>
                </div>
              </div>
              {#if getEscoltaNames(item).length > 0}
                <div class="flex justify-between">
                  <span class="note-label text-xs font-medium flex-shrink-0">Escoltas:</span>
                  <div class="overflow-x-auto flex-1 ml-2">
                    <div class="flex flex-wrap gap-1 justify-end">
                      {#each getEscoltaNames(item) as escolta}
                        <span class="note-escolta text-xs px-2 py-1 rounded">{escolta}</span>
                      {/each}
                    </div>
                  </div>
                </div>
              {/if}
            </div>
          </li>
        {/each}
      </ul>
    {:else}
        <div class="flex items-center justify-center h-32 text-blueGray-400 text-sm border-2 border-dashed border-blueGray-300 rounded-lg">
          <p class="text-center px-3">
            {#if pickerMode && status === PICKER_CLAIM_TO}
              Arrastra aquí un pedido autorizado para tomarlo
            {:else if pickerMode && status === PICKER_CLAIM_FROM}
              No hay pedidos autorizados por créditos
            {:else if pickerMode}
              No tienes pedidos en este momento
            {:else if isAdmin}
              Drop items here to change status to: {status}
            {:else}
              Only administrators can drag and drop orders
            {/if}
          </p>
        </div>
      {/if}
  </div>

  <div class={`rounded-b px-4 py-3 border-t mt-auto ${getStatusColor()}`}>
    <div class="text-blueGray-500 text-sm">
      Total: {viewItems.length} items
    </div>
  </div>
</div>

{#if pickerMode}
<PickerPanel
  {selectedItem}
  {closePane}
  {status}
  on:statusChanged={handlePickerStatusChanged}
  on:observUpdated={handleObservUpdated}
/>
{:else}
<RightPane 
  {selectedItem}
  {closePane}
  {status}
  on:updateLocation={handleLocationUpdate}
  on:updateEgresoEscolta={handleEgresoEscoltaUpdate}
  on:pickerUpdated={handlePickerUpdated}
/>
{/if}
